
/**
 * Service to handle File System Access API operations.
 * Allows creating physical folders and files on the user's local disk.
 */

let activeProjectHandle: any = null;

export const setProjectHandle = (handle: any) => {
  activeProjectHandle = handle;
};

export const getProjectHandle = () => activeProjectHandle;

export const createLocalProjectStructure = async (projectNumber: string, metadata: any) => {
  // Check browser support
  if (!('showDirectoryPicker' in window)) {
    alert("Your browser does not support direct Local File System access. Please use Chrome, Edge, or Opera.");
    return false;
  }

  try {
    // 1. Request User to Pick Root Folder
    const rootHandle = await (window as any).showDirectoryPicker({
      id: 'sst_projects_root',
      mode: 'readwrite',
      startIn: 'documents'
    });

    // 2. Create the Specific Project Folder
    const safeName = projectNumber.replace(/[^a-z0-9_\-]/gi, '_');
    const projectHandle = await rootHandle.getDirectoryHandle(safeName, { create: true });

    // Store handle globally for this session
    activeProjectHandle = projectHandle;

    // 3. Create Standardized Subdirectories (Revised V6.1 Structure)
    await projectHandle.getDirectoryHandle('01_Evidence_Images', { create: true });
    await projectHandle.getDirectoryHandle('02_Verified_Findings', { create: true });
    await projectHandle.getDirectoryHandle('03_Video_Logs', { create: true });
    await projectHandle.getDirectoryHandle('04_Telemetry_Data', { create: true });
    await projectHandle.getDirectoryHandle('05_Generated_Reports', { create: true });
    await projectHandle.getDirectoryHandle('06_Ground_Truth', { create: true });

    // 4. Create Project Manifest File
    const fileHandle = await projectHandle.getFileHandle('project_manifest.json', { create: true });
    const writable = await fileHandle.createWritable();

    const content = JSON.stringify({
      id: crypto.randomUUID(),
      projectNumber,
      createdAt: new Date().toISOString(),
      metadata,
      generator: "NDT-CHECK PRO V6.0",
      schemaVersion: "1.0"
    }, null, 2);

    await writable.write(content);
    await writable.close();

    return true;
  } catch (error: any) {
    if (error.name === 'AbortError') {
      return false;
    }
    console.error("File System Error:", error);
    alert(`Failed to create local folders: ${error.message}`);
    return false;
  }
};

/**
 * Saves a blob to a specific subdirectory within the active project.
 */
export const saveFileToProject = async (folderName: string, fileName: string, blob: Blob) => {
  if (!activeProjectHandle) return false;

  try {
    const subDirHandle = await activeProjectHandle.getDirectoryHandle(folderName, { create: true });
    const fileHandle = await subDirHandle.getFileHandle(fileName, { create: true });
    const writable = await fileHandle.createWritable();
    await writable.write(blob);
    await writable.close();
    return true;
  } catch (err) {
    console.error(`Failed to auto-save to ${folderName}:`, err);
    return false;
  }
};

/**
 * Reads all .json files from a project's raw evidence folder (01_Evidence_Images).
 * Each JSON represents one AI-analyzed frame which may contain multiple findings.
 * Returns records sorted by timestamp ascending.
 */
export const readEvidenceRecords = async () => {
  if (!activeProjectHandle) return [];
  const records: any[] = [];
  try {
    let evHandle = null;
    try { evHandle = await activeProjectHandle.getDirectoryHandle('01_Evidence_Images', { create: false }); } catch (e) {
      for await (const entry of activeProjectHandle.values()) {
        if (entry.kind === 'directory' && (entry.name.startsWith('01') || entry.name.toLowerCase().includes('evidence'))) {
          evHandle = entry; break;
        }
      }
    }
    if (!evHandle) return [];
    for await (const [name, handle] of (evHandle as any).entries()) {
      if (handle.kind === 'file' && (name as string).toLowerCase().endsWith('.json')) {
        try {
          const file = await (handle as any).getFile();
          const text = await file.text();
          records.push(JSON.parse(text));
        } catch (e) { }
      }
    }
  } catch (e) { }
  // Sort by timestamp ascending so we process chronologically
  return records.sort((a, b) => (a.timestamp || 0) - (b.timestamp || 0));
};

/** Backwards-compatible alias for readEvidenceRecords */
export const readDiscoveryRecords = readEvidenceRecords;

/**
 * Reads all .json files from a project's verified folder (02_Verified_Findings).
 * This is the primary source for the official Audit Results and Mission Map.
 */
export const readProjectRecords = async () => {
  if (!activeProjectHandle) return [];
  const records: any[] = [];
  try {
    let evHandle = null;
    // Attempt exact match first
    try { evHandle = await activeProjectHandle.getDirectoryHandle('02_Verified_Findings', { create: false }); } catch (e) {
      // Fuzzy search for "02" or "verified"
      for await (const entry of activeProjectHandle.values()) {
        if (entry.kind === 'directory') {
          const n = entry.name.toLowerCase();
          if (n.startsWith('02') || (n.includes('verified') && !n.includes('evidence'))) {
            evHandle = entry;
            break;
          }
        }
      }
    }

    if (!evHandle) return [];

    for await (const [name, handle] of (evHandle as any).entries()) {
      if (handle.kind === 'file' && (name as string).toLowerCase().endsWith('.json')) {
        try {
          const file = await (handle as any).getFile();
          const text = await file.text();
          records.push(JSON.parse(text));
        } catch (e) {
          console.warn('GEA-NDT: Could not read project record:', name);
        }
      }
    }
  } catch (e) { }
  return records;
};

/**
 * Reads all .json files from a project's ground truth folder (06_Ground_Truth).
 * This is used specifically for human-verified AI training data ("AI Learnings").
 */
export const readGroundTruthRecords = async () => {
  if (!activeProjectHandle) return [];
  const records: any[] = [];
  try {
    let gtHandle = null;
    try { gtHandle = await activeProjectHandle.getDirectoryHandle('06_Ground_Truth', { create: false }); } catch (e) {
      for await (const entry of activeProjectHandle.values()) {
        if (entry.kind === 'directory') {
          const n = entry.name.toLowerCase();
          if (n.startsWith('06') || n.includes('ground_truth') || n.includes('learnings')) {
            gtHandle = entry;
            break;
          }
        }
      }
    }

    if (!gtHandle) return [];

    for await (const [name, handle] of (gtHandle as any).entries()) {
      if (handle.kind === 'file' && (name as string).toLowerCase().endsWith('.json')) {
        try {
          const file = await (handle as any).getFile();
          const text = await file.text();
          records.push(JSON.parse(text));
        } catch (e) {
          console.warn('GEA-NDT: Could not read ground truth file:', name);
        }
      }
    }
  } catch (e) { }
  return records;
};


/**
 * Loads a project by reading its manifest from a selected directory.
 */
export const loadProjectFromLocal = async () => {
  if (!('showDirectoryPicker' in window)) {
    alert("Your browser does not support direct Local File System access.");
    return null;
  }

  try {
    const projectHandle = await (window as any).showDirectoryPicker({
      id: 'sst_projects_root',
      mode: 'readwrite',
      startIn: 'documents'
    });

    const fileHandle = await projectHandle.getFileHandle('project_manifest.json');
    const file = await fileHandle.getFile();
    const content = await file.text();
    const manifest = JSON.parse(content);

    // Store handle globally
    activeProjectHandle = projectHandle;

    return {
      handle: projectHandle,
      manifest
    };
  } catch (err: any) {
    if (err.name === 'AbortError') return null;
    console.error("Load Project Error:", err);
    alert("Selected folder is not a valid NDT-CHECK project or manifest is missing.");
    return null;
  }
};

/**
 * Saves a session checkpoint to the project root.
 */
export const saveSessionCheckpoint = async (data: any) => {
  if (!activeProjectHandle) return false;
  try {
    const fileHandle = await activeProjectHandle.getFileHandle('session_checkpoint.json', { create: true });
    const writable = await fileHandle.createWritable();
    await writable.write(JSON.stringify({ ...data, updatedAt: new Date().toISOString() }, null, 2));
    await writable.close();
    return true;
  } catch (err) {
    console.error("Failed to save session checkpoint:", err);
    return false;
  }
};

/**
 * Retrieves a session checkpoint from the project root.
 */
export const getSessionCheckpoint = async () => {
  if (!activeProjectHandle) return null;
  try {
    const fileHandle = await activeProjectHandle.getFileHandle('session_checkpoint.json');
    const file = await fileHandle.getFile();
    const text = await file.text();
    return JSON.parse(text);
  } catch (err) {
    return null;
  }
};
/**
 * Discovers the primary assets in a project to enable "One-Click Resume"
 */
export const discoverProjectAssets = async () => {
  if (!activeProjectHandle) return null;

  const assets: {
    video?: File;
    telemetry?: File;
    pointCloud?: File;
    images?: File[];
  } = {};

  const videos: File[] = [];
  const telemetries: File[] = [];
  const pointClouds: File[] = [];
  const images: File[] = [];

  try {
    async function searchHandleForAssets(dirHandle: any, depth = 0) {
      if (depth > 10) return; // Prevent excessive recursion depth

      for await (const entry of dirHandle.values()) {
        try {
          if (entry.kind === 'file') {
            const name = entry.name.toLowerCase();

            if (name.endsWith('.mp4') || name.endsWith('.mov') || name.endsWith('.avi') || name.endsWith('.mkv') || name.endsWith('.wmv')) {
              videos.push(await entry.getFile());
            }
            else if (name.endsWith('.csv')) {
              telemetries.push(await entry.getFile());
            }
            else if (name.endsWith('.las') || name.endsWith('.laz') || name.endsWith('.xyz')) {
              pointClouds.push(await entry.getFile());
            }
            else if (name.match(/\.(jpg|jpeg|png|webp)$/i)) {
              images.push(await entry.getFile());
            }
          } else if (entry.kind === 'directory') {
            await searchHandleForAssets(entry, depth + 1);
          }
        } catch (e) {
          // Skip unreadable files/folders
        }
      }
    }

    await searchHandleForAssets(activeProjectHandle);

    if (videos.length > 0) {
      videos.sort((a, b) => b.size - a.size);
      assets.video = videos[0];
    }
    if (telemetries.length > 0) {
      telemetries.sort((a, b) => b.size - a.size);
      assets.telemetry = telemetries[0];
    }
    if (pointClouds.length > 0) {
      pointClouds.sort((a, b) => b.size - a.size);
      assets.pointCloud = pointClouds[0];
    }
    if (images.length > 0) {
      assets.images = images;
    }

    return assets;
  } catch (err) {
    console.error("Asset discovery failed:", err);
    return null;
  }
};
